/**
 * Fetches per-alarm detail for dev mode, only while `enabled`. The BFF serves it to sysadmins
 * only and shares one CloudWatch fetch per minute across callers, so polling adds no load per tab.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AtmsApiClient } from '@amzn/et-console-components';
import { AlarmDetailsResponse, FetchErrorKind, toFetchErrorKind } from '../types';
import { REFRESH_INTERVAL_MS } from '../components/constants';

export interface AlarmDetailsHookState {
  isLoading: boolean;
  data: AlarmDetailsResponse | null;
  error: FetchErrorKind | null;
}

export function useAlarmDetails(enabled: boolean): AlarmDetailsHookState {
  const [state, setState] = useState<AlarmDetailsHookState>({
    isLoading: false,
    data: null,
    error: null,
  });
  const requestIdRef = useRef(0);

  const fetchData = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setState(prev => ({ ...prev, isLoading: prev.data === null, error: null }));

    AtmsApiClient.httpGet('/api/observability/alarm-details')
      .then((result: AlarmDetailsResponse) => {
        if (requestId === requestIdRef.current) {
          setState({ isLoading: false, data: result, error: null });
        }
      })
      .catch((err: { status?: number; response?: { status?: number } }) => {
        if (requestId !== requestIdRef.current) return;
        setState(prev => ({ ...prev, isLoading: false, error: toFetchErrorKind(err) }));
      });
  }, []);

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current++;
      setState(prev => ({ ...prev, isLoading: false }));
      return undefined;
    }
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, fetchData]);

  return state;
}
